import { INQUIRY_ITEM, INQUIRY_STATUS } from '@/types'
import { parseFormFields } from '@/utils/parsers'
import { endpoints } from '@/utils/endpoints'

function handlePageNameForMixpanel(pageName: string, embedded: boolean) {
  if (embedded) return 'Inquiry Details'

  if (pageName === 'index') {
    return 'Inquiry List'
  } else if (pageName === 'inquiry-details') {
    return 'Inquiry Details'
  } else if (pageName === 'inquiry-desktop-details') {
    return 'Inquiry Details'
  } else if (pageName === 'inquiry-messages') {
    return 'Inquiry Messages'
  } else if (pageName === 'inquiry-notes') {
    return 'Inquiry Notes'
  } else if (pageName === 'inquiry-proposals') {
    return 'Inquiry Payment'
  } else if (pageName === 'notifications') {
    return 'Notifications Page'
  } else if (pageName === 'settings') {
    return 'Settings Page'
  }
  return 'Expanded Notification'
}

function prepareInquiryForMixpanel(inquiry: INQUIRY_ITEM) {
  const firstName = inquiry.name.substring(0, inquiry.name.indexOf(' '))
  const lastName = inquiry.name.substring(inquiry.name.indexOf(' ') + 1)
  const formFields = parseFormFields(inquiry.form_fields)
  const textingPermission = formFields.texting_permission ?? null

  type pageName = string | null
  return {
    page_name: null as pageName,
    inquiry_id: inquiry.id,
    status:
      inquiry.status ||
      inquiry.pick_up_order_status ||
      inquiry.delivery_order_status,
    first_name: firstName,
    last_name: lastName,
    type: inquiry.type,
    occasion: inquiry.party_type || null,
    booked_number_of_people: inquiry.in_party || null,
    space: inquiry.space_preference || null,
    date: inquiry.day,
    month: inquiry.month,
    year: inquiry.year,
    email: inquiry.email,
    phone: inquiry.phone || null,
    texting_permission: textingPermission,
  }
}

async function sendMixPanelEvent(
  event: string,
  event_data: {
    [key: string]: any
  },
) {
  const deviceInfo = localStorage.getItem('device_info')
  const deviceToken = localStorage.getItem('device-token')

  const authStore = useAuthStore()
  const appStore = useAppStore()

  if (appStore.isAdmin) return

  event_data.spot_id = authStore.spotId

  useApi(endpoints().mixpanel(authStore.spotId)).post({
    event_name: appStore.spotHopperEmbedded
      ? event.replace(' in Inquiry App', '')
      : event,
    event_data: {
      ...event_data,
      device: deviceInfo ?? null,
      device_token: deviceToken ?? null,
    },
  })
}

export function sendViewedPageEvent(pageName: string) {
  const appStore = useAppStore()
  if (appStore.spotHopperEmbedded) return // for inquiry details page do not send page view

  const page_name = handlePageNameForMixpanel(
    pageName,
    appStore.spotHopperEmbedded,
  )

  const event_data = {
    page_name,
  }
  sendMixPanelEvent('Viewed Admin in Inquiry App page', event_data)
}

export function sendLoggedInEvent(
  message: 'Success login' | 'Failed',
  loginMethod: string,
) {
  const event_data = {
    message,
    login_method: loginMethod,
  }
  sendMixPanelEvent('Logged into Inquiry App ', event_data)
}

export function sendLoggedOutEvent(action: 'Yes' | 'No') {
  const event_data = {
    action,
  }
  sendMixPanelEvent('Clicked to Log Out', event_data)
}

export function sendClickedInquiryPreviewEvent(inquiry: INQUIRY_ITEM) {
  const event_data = prepareInquiryForMixpanel(inquiry)

  event_data.page_name = null

  sendMixPanelEvent('Clicked Inquiry Preview in Inquiry App', event_data)
}

export function sendDeletedInquiryEvent(
  inquiry: INQUIRY_ITEM,
  pageName: 'Preview' | 'Inquiry Details',
) {
  const event_data = prepareInquiryForMixpanel(inquiry)

  event_data.page_name = pageName

  sendMixPanelEvent('Deleted Inquiry in Inquiry App', event_data)
}

export function sendAddedInquiryEvent(
  inquiry: INQUIRY_ITEM,
  pageName: 'Preview' | 'Inquiry Details',
) {
  const event_data = prepareInquiryForMixpanel(inquiry)

  event_data.page_name = pageName

  sendMixPanelEvent('Added Inquiry in Inquiry App', event_data)
}

export function sendEditedInquiryEvent(inquiry: INQUIRY_ITEM) {
  const event_data = prepareInquiryForMixpanel(inquiry)

  event_data.page_name = 'Inquiry Details'

  sendMixPanelEvent('Edited Inquiry in Inquiry App', event_data)
}

export function clickedOnChangeRecipient(inquiry: INQUIRY_ITEM) {
  const event_data = prepareInquiryForMixpanel(inquiry)

  event_data.page_name = 'Inquiry Details'

  sendMixPanelEvent('Clicked on Change Recipient', event_data)
}

export function sendSpotRespondedAndChangedStatusEvent(
  inquiry: INQUIRY_ITEM,
  comment: string,
  oldStatus: string,
  responseSourceType: string | null = null,
  responseEdited: boolean | null = null,
) {
  const appStore = useAppStore()
  const inquiriesStore = useInquiriesStore()
  const sendResponseAsTextMessage = inquiry.send_text_message
  const inquiryComments = JSON.parse(JSON.stringify(inquiriesStore.comments))

  const numOfCustomerMessages = inquiryComments.length
    ? inquiryComments.filter(
        (comment: { author_role: string }) => comment.author_role == 'customer',
      ).length + 1
    : 0

  const numOfConsumerMessages = inquiryComments.length
    ? inquiryComments.filter(
        (comment: { author_role: string }) => comment.author_role == 'consumer',
      ).length
    : 0

  const numOfDocuments = inquiryComments[inquiryComments.length - 1].documents
    ? Object.keys(inquiryComments[inquiryComments.length - 1].documents).length
    : 0

  type timeToRespond = number | null

  const event_data = {
    inquiry_id: inquiry.id,
    also_send_response_as_text_message: sendResponseAsTextMessage,
    attachment_count: inquiriesStore.attachments.length,
    customer_message_num: numOfCustomerMessages,
    inquiry_age: (Date.now() - Date.parse(inquiry.created_at)) / 1000,
    inquiry_comment: comment,
    inquiry_type: inquiry.type,
    new_status:
      inquiry.status ||
      inquiry.pick_up_order_status ||
      inquiry.delivery_order_status,
    num_of_prior_consumer_messages: numOfConsumerMessages,
    old_status: oldStatus,
    time_to_respond: null as timeToRespond,
    num_of_documents: numOfDocuments,
    response_source_type: responseSourceType,
    response_edited: responseEdited,
    is_standard_response_suggested:
      appStore.spotInfo.standard_responses_enabled,
  }

  if (inquiryComments.length) {
    event_data.time_to_respond =
      (Date.now() -
        Date.parse(
          inquiryComments.sort(
            (a: any, b: any) =>
              Date.parse(a.created_at) - Date.parse(b.created_at),
          )[inquiryComments.length - 1].created_at,
        )) /
      1000
  }

  sendMixPanelEvent(
    'Spot Responded and Changed Inquiry Status in Inquiry App',
    event_data,
  )
}

export function sendChangedInquiryStatusEvent(
  inquiry: INQUIRY_ITEM,
  oldStatus: string,
  pageName: string,
) {
  const event_data = {
    page_name: handlePageNameForMixpanel(
      pageName,
      useAppStore().spotHopperEmbedded,
    ),
    inquiry_id: inquiry.id,
    inquiry_age: (Date.now() - Date.parse(inquiry.created_at)) / 1000,
    inquiry_type: inquiry.type,
    new_status:
      inquiry.status ||
      inquiry.pick_up_order_status ||
      inquiry.delivery_order_status,
    old_status: oldStatus,
  }

  sendMixPanelEvent('Changed Inquiry Status in Inquiry App', event_data)
}
export function sendProposalCreatedEvent(proposal: {
  id: number
  description: string
  payment_type: string
  payment_name: string
  payment_instructions: string
  payment_amount: string
  due_date: string
}) {
  const event_data = {
    proposal_id: proposal.id,
    description: proposal.description,
    payment_type: proposal.payment_type,
    payment_name: proposal.payment_name,
    payment_instructions: proposal.payment_instructions,
    payment_amount: proposal.payment_amount,
    due_date: proposal.due_date,
  }

  sendMixPanelEvent('Proposal Created in Inquiry App', event_data)
}
export function sendProposalEditedEvent(proposal: {
  proposal_id: number
  description_before: string
  description_now: string
  payment_type_before: string
  payment_type_now: string
  payment_name_before: string
  payment_name_now: string
  payment_instructions_before: string
  payment_instructions_now: string
  payment_amount_before: number
  payment_amount_now: number
  due_date_before: string
  due_date_now: string
}) {
  const event_data = {
    proposal_id: proposal.proposal_id,
    description_before: proposal.description_before,
    description_now: proposal.description_now,
    payment_type_before: proposal.payment_type_before,
    payment_type_now: proposal.payment_type_now,
    payment_name_before: proposal.payment_name_before,
    payment_name_now: proposal.payment_name_now,
    payment_instructions_before: proposal.payment_instructions_before,
    payment_instructions_now: proposal.payment_instructions_before,
    payment_amount_before: proposal.payment_amount_before,
    payment_amount_now: proposal.payment_amount_now,
    due_date_before: proposal.due_date_before,
    due_date_now: proposal.due_date_now,
  }

  sendMixPanelEvent('Proposal Edited in Inquiry App', event_data)
}

export function sendPaymentRefundedEvent(proposal: {
  payment_type: string
  proposal_id: number
}) {
  const event_data = {
    page_name: 'Inquiry Payments',
    payment_type: proposal.payment_type,
    proposal_id: proposal.proposal_id,
  }

  sendMixPanelEvent('Payment Refunded in Inquiry App', event_data)
}

export function sendPaymentCapturedEvent(proposal: {
  payment_type: string
  proposal_id: number
}) {
  const event_data = {
    page_name: 'Inquiry Payments',
    payment_type: proposal.payment_type,
    proposal_id: proposal.proposal_id,
  }

  sendMixPanelEvent('Payment Captured in Inquiry App', event_data)
}

export function getInitialInquiryStatus(inquiry: INQUIRY_ITEM) {
  return (inquiry.status ||
    inquiry.delivery_order_status ||
    inquiry.pick_up_order_status) as INQUIRY_STATUS
}

export function sendShownNextStepsEvent(
  inquiry: INQUIRY_ITEM,
  buttonType: 'Respond' | 'Follow-up',
) {
  const event_data = {
    link_name: buttonType,
    inquiry_id: inquiry.id,
    inquiry_type: inquiry.type,
  }

  sendMixPanelEvent(
    'Shown Next Step for Inquiry Link in Inquiry App',
    event_data,
  )
}

export function sendClickedNextStepsEvent(
  inquiry: INQUIRY_ITEM,
  buttonType: 'Respond' | 'Follow-up',
) {
  const event_data = {
    link_name: buttonType,
    inquiry_id: inquiry.id,
    inquiry_type: inquiry.type,
  }

  sendMixPanelEvent(
    'Clicked on Next Step for Inquiry Link in Inquiry App',
    event_data,
  )
}

export function sendClickedOnNotification(
  inquiry: INQUIRY_ITEM,
  action: 'see details' | 'respond' | 'confirm',
  notification_type: 'new inquiry' | 'friendly reminder' | 'new message',
) {
  const event_data = {
    action,
    notification_type,
    inquiry_id: inquiry.id,
    type: inquiry.type,
    status:
      inquiry.status ||
      inquiry.pick_up_order_status ||
      inquiry.delivery_order_status,
    first_name: inquiry.name.substring(0, inquiry.name.indexOf(' ')),
    last_name: inquiry.name.substring(inquiry.name.indexOf(' ') + 1),
    date: inquiry.day,
    month: inquiry.month,
    year: inquiry.year,
    email: inquiry.email,
  }

  sendMixPanelEvent('Clicked on Notification in Inquiry App', event_data)
}

export function sendAddDocumentEvent(inquiry: INQUIRY_ITEM) {
  const event_data = {
    page_name: 'Inquiry Details',
    inquiry_id: inquiry.id,
    inquiry_type: inquiry.type,
  }

  sendMixPanelEvent('Clicked Add Document or E-Contract Button', event_data)
}

export function sendDeleteDocumentEvent(
  inquiry: INQUIRY_ITEM,
  document: INQUIRY_DOCUMENT,
) {
  const event_data = {
    page_name: 'Inquiry Details',
    document_id: document.id,
    document_name: document.file_name,
    document_status: document.status,
    inquiry_id: inquiry.id,
    inquiry_type: inquiry.type,
  }

  sendMixPanelEvent('Deleted Document', event_data)
}

export function sendNudgeShownEvent() {
  const event_data = {
    action_name: 'Event Planner Upgrade',
    page_name: 'Inquiry Details',
  }

  sendMixPanelEvent('Nudge Shown', event_data)
}

export function sendNudgeClickedEvent() {
  const event_data = {
    button_name: 'Event Planner Upgrade',
    page_name: 'Inquiry Details',
  }

  sendMixPanelEvent('Clicked Button', event_data)
}

export function sendCloseSurveyEvent(
  pageName: string,
  feedback: {key: string, question_text: string},
  inquiryType: string,
) {
  const event_data = {
    page_name: pageName,
    topic: feedback.key,
    question: feedback.question_text,
    survey_type: 'Emoji',
    inquiry_type: inquiryType,
  }

  sendMixPanelEvent(
    'Closed Customer Feedback Without Response',
    event_data,
  )
}

export function sendCustomerFeedbackFollowUpShownEvent(
  pageName: string,
  feedback: { key: string },
  inquiryType: string,
) {
  const event_data = {
    page_name: pageName,
    topic: feedback.key,
    trigger_source: 'customer_feedback_negative',
    inquiry_type: inquiryType,
  }

  sendMixPanelEvent(
    'Customer Feedback Follow-up Shown', 
    event_data)
}

export function sendCloseCustomerFeedbackWithoutResponseEvent(
  pageName: string,
  feedback: { key: string; question_text: string },
  inquiryType: string,
) {
  const event_data = {
    page_name: pageName,
    topic: feedback.key,
    question: feedback.question_text,
    survey_type: 'Emoji',
    inquiry_type: inquiryType,
  }

  sendMixPanelEvent(
    'Customer Feedback Follow-up Closed Without Response',
    event_data,
  )
}

export function sendFollowUpThankYouShownEvent(
  pageName: string,
  feedback: { key: string },
  inquiryType: string,
) {
  const event_data = {
    page_name: pageName,
    topic: feedback.key,
    inquiry_type: inquiryType,
  }

  sendMixPanelEvent('Customer Feedback Follow-up Thank You Shown', event_data)
}

export function sendInquiryListFilterSelectedEvent(filterValue: string) {
  const event_data = {
    page_name: 'Inquiry List',
    filter_type: 'time',
    filter_value: filterValue.toLowerCase(),
  }

  sendMixPanelEvent('Inquiry List Filter Selected', event_data)
}

export function sendShownNextStepForInquiryLinkEvent(
  inquiryId: number,
  inquiryType: string,
  btnType: string,
) {
  const event_data = {
    page_name: 'Inquiry List',
    link_name: btnType,
    inquiry_id: inquiryId,
    inquiry_type: inquiryType,
  }

  sendMixPanelEvent('Shown Next Step for Inquiry Link', event_data)
}

export function sendClickedNextStepForInquiryLinkEvent(
  inquiryId: number,
  inquiryType: string,
  btnType: string,
) {
  const event_data = {
    page_name: 'Inquiry List',
    link_name: btnType,
    inquiry_id: inquiryId,
    inquiry_type: inquiryType,
  }

  sendMixPanelEvent('Clicked on Next Step for Inquiry Link', event_data)
}

export function sendInquiryListConsumerSearchUsedEvent(
  searchQuery: string,
  queryLength: number,
  resultsCount: number,
	filterValue:  string,
) {
  const event_data = {
    page_name: 'Inquiry List',
    search_query: searchQuery,
  	query_length: queryLength,
  	results_count: resultsCount,
	  filter_value: filterValue.toLowerCase()
  }

  sendMixPanelEvent('Inquiry List Consumer Search Used', event_data)
}
